import React, { createContext, useContext, ReactNode, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getApiClient, OptionLabelItem, OrganOptionLabelItem, VoteOptionLabelItem, GeoLabelsItem, MetricLabelsDoc } from '../api';
import { ENABLE_STATS } from '../Constants';

function labelsFrom<T>(result: PromiseSettledResult<{ labels?: T }>, context: string): T {
    if (result.status === 'fulfilled') {
        return result.value.labels ?? ({} as T);
    }
    console.error(`Chyba při načítání lokalizačních labelů (${context}):`, result.reason);
    return {} as T;
}

export interface LocalizationLabelsContextValue {
    organOptionLabels: Record<string, OptionLabelItem>;
    municipalityOptionLabels: Record<string, OptionLabelItem>;
    municipalityNameOptionLabels: Record<string, OptionLabelItem>;
    voteOptionLabels: Record<string, VoteOptionLabelItem>;
    attendanceOptionLabels: Record<string, string>;
    voteResultOptionLabels: Record<string, string>;
    organNameOptionLabels: Record<string, OrganOptionLabelItem>;
    geoNameOptionLabels: Record<string, GeoLabelsItem>;
    statsLabels: MetricLabelsDoc;
    loading: boolean;
}

const LocalizationLabelsContext = createContext<LocalizationLabelsContextValue | undefined>(undefined);

export const useLocalizationLabels = (): LocalizationLabelsContextValue => {
    const context = useContext(LocalizationLabelsContext);
    if (context === undefined) {
        throw new Error('useLocalizationLabels must be used within a LocalizationLabelsProvider');
    }
    return context;
};

interface LocalizationLabelsProviderProps {
    children: ReactNode;
}

export const LocalizationLabelsProvider: React.FC<LocalizationLabelsProviderProps> = ({ children }) => {
    const { i18n, ready } = useTranslation();
    const currentLocale = (i18n.resolvedLanguage ?? i18n.language ?? 'cs');

    const [organOptionLabels, setOrganOptionLabels] = useState<Record<string, OptionLabelItem>>({});
    const [municipalityOptionLabels, setMunicipalityOptionLabels] = useState<Record<string, OptionLabelItem>>({});
    const [municipalityNameOptionLabels, setMunicipalityNameOptionLabels] = useState<Record<string, OptionLabelItem>>({});
    const [voteOptionLabels, setVoteOptionLabels] = useState<Record<string, VoteOptionLabelItem>>({});
    const [attendanceOptionLabels, setAttendanceOptionLabels] = useState<Record<string, string>>({});
    const [voteResultOptionLabels, setVoteResultOptionLabels] = useState<Record<string, string>>({});
    const [organNameOptionLabels, setOrganNameOptionLabels] = useState<Record<string, OrganOptionLabelItem>>({});
    const [geoNameOptionLabels, setGeoNameOptionLabels] = useState<Record<string, GeoLabelsItem>>({});
    const [statsLabels, setStatsLabels] = useState<MetricLabelsDoc>({
        scope: {},
        metrics: {},
        units: {},
        dim_values: {},
        dims: {},
        source: {}
    })

    const [loading, setLoading] = useState<boolean>(true);

    useEffect(() => {
        if (!ready) {
            return;
        }
        const fetchLabels = async () => {
            setLoading(true);
            const api = getApiClient();

            // Geo/stats labels depend on the backend's optional statsdb Mongo connection
            // (USE_STATS_DB=1). When it's not enabled, don't even issue the request — it
            // would only ever fail, and (being awaited alongside everything else) must not
            // be allowed to affect the other, unrelated label state set below.
            const geoLabelsRequest = ENABLE_STATS ? api.getGeoLabelsByLang(currentLocale) : Promise.resolve({ labels: {} as Record<string, GeoLabelsItem> });
            const statsLabelsRequest = ENABLE_STATS ? api.getStatsLabelsByLang(currentLocale) : Promise.resolve({ labels: {} as MetricLabelsDoc });

            const [
                organLabelsMap,
                municipalityLabelsMap,
                municipalityNameLabelsMap,
                voteOptionLabelsMap,
                attendanceOptionLabelsMap,
                voteResultOptionLabelsMap,
                organNameOptionLabelsMap,
                geoLabelsMap,
                statsLabelsMap,
            ] = await Promise.allSettled([
                api.getTypOrganuOptionsLanguageByLang(currentLocale),
                api.getTypMunicipalityOptionsLanguageByLang(currentLocale),
                api.getMunicipalityNameOptionsLanguageByLang(currentLocale),
                api.getVoteOptionsLanguageByLang(currentLocale),
                api.getAttendanceOptionsLanguageByLang(currentLocale),
                api.getVoteResultOptionsLanguageByLang(currentLocale),
                api.getOrganNameOptionsLanguageByLang(currentLocale),
                geoLabelsRequest,
                statsLabelsRequest,
            ]);

            setOrganOptionLabels(labelsFrom(organLabelsMap, 'typ_organu'));
            setMunicipalityOptionLabels(labelsFrom(municipalityLabelsMap, 'typ_municipality'));
            setMunicipalityNameOptionLabels(labelsFrom(municipalityNameLabelsMap, 'municipality_name'));
            setVoteOptionLabels(labelsFrom(voteOptionLabelsMap, 'vote_options'));
            setAttendanceOptionLabels(labelsFrom(attendanceOptionLabelsMap, 'attendance_options'));
            setVoteResultOptionLabels(labelsFrom(voteResultOptionLabelsMap, 'vote_result_options'));
            setOrganNameOptionLabels(labelsFrom(organNameOptionLabelsMap, 'organ_name'));
            setGeoNameOptionLabels(labelsFrom(geoLabelsMap, 'geo_labels'));
            setStatsLabels(labelsFrom(statsLabelsMap, 'stats_labels'));

            setLoading(false);
        };

        void fetchLabels();
    }, [currentLocale, ready]);

    const value = useMemo(
        () => ({
            organOptionLabels,
            municipalityOptionLabels,
            municipalityNameOptionLabels,
            voteOptionLabels,
            attendanceOptionLabels,
            voteResultOptionLabels,
            organNameOptionLabels,
            geoNameOptionLabels,
            statsLabels,
            loading
        }),
        [
            organOptionLabels,
            municipalityOptionLabels,
            municipalityNameOptionLabels,
            voteOptionLabels,
            attendanceOptionLabels,
            voteResultOptionLabels,
            organNameOptionLabels,
            geoNameOptionLabels,
            statsLabels,
            loading
        ]
    );

    return (
        <LocalizationLabelsContext.Provider value={value}>
            {children}
        </LocalizationLabelsContext.Provider>
    );
};

export default LocalizationLabelsContext;
